
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getBilingualValue } from '@/utils/bilingual-helpers';
import i18n from '@/i18n/config';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Calendar, MapPin, Users, Search } from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import EventFilters, { EventFilterState } from '@/components/events/EventFilters';

interface PublicEvent {
  id: string;
  title: string;
  title_en: string | null;
  title_zh: string | null;
  description: string;
  description_en: string | null;
  description_zh: string | null;
  event_date: string;
  duration_minutes: number;
  location_type: string;
  location_details: string | null;
  price: number;
  currency: string;
  max_participants: number;
  cover_image_url: string | null;
  tags: string[];
  service_track: string;
  practitioner_id: string;
  timezone: string | null;
  location_city: string | null;
}

const Events: React.FC = () => {
  const { t } = useTranslation('events');
  const lang = i18n.language;
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [regCounts, setRegCounts] = useState<Record<string, number>>({});
  const [filters, setFilters] = useState<EventFilterState>({
    locationType: 'all',
    dateRange: 'all',
    priceType: 'all',
    selectedTags: [],
  });

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('group_events')
        .select('*')
        .eq('status', 'published')
        .gte('event_date', new Date().toISOString())
        .order('event_date', { ascending: true });

      if (!error && data) {
        setEvents(data.map(e => ({ ...e, tags: e.tags || [] })));
        const ids = data.map(e => e.id);
        if (ids.length > 0) {
          const { data: regs } = await supabase
            .from('group_event_registrations')
            .select('event_id')
            .in('event_id', ids)
            .in('status', ['registered', 'attended']);
          if (regs) {
            const counts: Record<string, number> = {};
            regs.forEach(r => { counts[r.event_id] = (counts[r.event_id] || 0) + 1; });
            setRegCounts(counts);
          }
        }
      }
      setLoading(false);
    };
    fetchEvents();
  }, []);

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    events.forEach(e => e.tags.forEach(tag => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [events]);

  const filtered = useMemo(() => {
    const now = new Date();
    return events.filter(e => {
      // Text search
      if (search) {
        const q = search.toLowerCase();
        const title = getBilingualValue(lang, e.title_en, e.title_zh, e.title) || '';
        if (!title.toLowerCase().includes(q) && !(e.tags || []).some(tag => tag.toLowerCase().includes(q))) {
          return false;
        }
      }
      // Location type
      if (filters.locationType !== 'all' && e.location_type !== filters.locationType) return false;
      // Price
      if (filters.priceType === 'free' && e.price > 0) return false;
      if (filters.priceType === 'paid' && e.price <= 0) return false;
      // Tags
      if (filters.selectedTags.length > 0 && !filters.selectedTags.some(t => e.tags.includes(t))) return false;
      // Date range
      if (filters.dateRange !== 'all') {
        const eventDate = new Date(e.event_date);
        if (filters.dateRange === 'this_week') {
          const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
          if (eventDate > weekEnd) return false;
        } else if (filters.dateRange === 'this_month') {
          const monthEnd = endOfMonth(now);
          if (eventDate > monthEnd) return false;
        } else if (filters.dateRange === 'next_month') {
          const nextMonthStart = startOfMonth(addMonths(now, 1));
          const nextMonthEnd = endOfMonth(addMonths(now, 1));
          if (eventDate < nextMonthStart || eventDate > nextMonthEnd) return false;
        }
      }
      return true;
    });
  }, [events, search, filters, lang]);

  const formatTz = (tz: string | null) => {
    if (!tz) return '';
    try {
      const short = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName');
      return short?.value || '';
    } catch { return ''; }
  };

  return (
    <>
      <Helmet>
        <title>{t('pageTitle')} | Resonow</title>
        <meta name="description" content={t('pageDescription')} />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20 pb-12 px-4 max-w-7xl mx-auto w-full">
          <div className="space-y-6">
            <div className="text-center space-y-3">
              <h1 className="text-3xl font-bold">{t('title')}</h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">{t('subtitle')}</p>
            </div>

            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-10"
                placeholder={t('searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <EventFilters filters={filters} onChange={setFilters} availableTags={availableTags} />

            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-16">{t('noEvents')}</p>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map(event => {
                  const title = getBilingualValue(lang, event.title_en, event.title_zh, event.title) || t('untitled');
                  const desc = getBilingualValue(lang, event.description_en, event.description_zh, event.description) || '';
                  const regCount = regCounts[event.id] || 0;
                  const spotsLeft = event.max_participants - regCount;
                  const tzAbbr = formatTz(event.timezone);

                  return (
                    <Link key={event.id} to={`/events/${event.id}`} className="block group">
                      <div className="border rounded-lg overflow-hidden bg-card shadow-sm hover:shadow-lg transition-shadow">
                        {event.cover_image_url ? (
                          <img src={event.cover_image_url} alt={title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform" />
                        ) : (
                          <div className="w-full h-48 bg-muted flex items-center justify-center">
                            <Calendar className="h-12 w-12 text-muted-foreground/30" />
                          </div>
                        )}
                        <div className="p-5 space-y-3">
                          <h3 className="font-semibold text-lg line-clamp-2 group-hover:text-primary transition-colors">{title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">{desc}</p>

                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {format(new Date(event.event_date), 'MMM d, yyyy · HH:mm')}
                              {tzAbbr && <span className="ml-1 text-xs opacity-70">({tzAbbr})</span>}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span>
                              {event.location_type === 'virtual' ? `🌐 ${t('online')}` : `📍 ${event.location_city || event.location_details || t('inPerson')}`}
                            </span>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="font-medium text-base">
                              {event.price > 0 ? `${event.currency} ${event.price}` : t('free')}
                            </span>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Users className="h-4 w-4" />
                              <span>{regCount}/{event.max_participants}</span>
                              {spotsLeft <= 0 && <Badge variant="destructive" className="text-xs ml-1">{t('full')}</Badge>}
                            </div>
                          </div>

                          {event.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {event.tags.slice(0, 3).map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Events;
